import { useState } from 'react';
import { useEstadoPreservado } from '@/lib/estadoDeSessao';
import { formatDistanceToNowStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useConversas } from '@/conversas/ConversasProvider';
import {
  RESPONSAVEL_SEM,
  RESPONSAVEL_TODOS,
  agruparPorEstado,
  contarNaoLidas,
  temFiltroAtivo,
} from '@/conversas/filtros';
import { nomeDoContato } from '@/conversas/contato';
import { legendaDoCanal, podeIniciarConversa } from '@/conversas/canais';
import { mostraDono } from '@/conversas/estadoAtendimento';
import { PAPEIS_NUMERO, PAPEIS_POR_ESPACO } from '@/lib/constants';
import { assumirConversa } from '@/services/conversasService';
import { podeAtender, rotuloDaReserva } from '@/conversas/reserva';
import { avisoDaPuxada } from '@/conversas/avisoPosse';
import { clinicaDeUmaSdr } from '@/leads/posseTela';
import { rotuloDaEntrega } from '@/conversas/entrega';
import AvatarContato from '@/components/conversas/AvatarContato';
import DialogoIniciarConversa from '@/components/conversas/DialogoIniciarConversa';
import PainelConfiguracoes from '@/components/conversas/PainelConfiguracoes';
import { useNotificacaoSom } from '@/conversas/useNotificacaoSom';
import { useSecoesInbox } from '@/conversas/useSecoesInbox';
import { secaoPadrao, secoesVisiveisOrdenadas } from '@/conversas/secoesInbox';

const quando = (valor) => {
  const data = typeof valor?.toDate === 'function' ? valor.toDate() : null;
  if (!data) return '';
  return formatDistanceToNowStrict(data, { locale: ptBR, addSuffix: false });
};

/** Setor de onde a conversa veio, para o chip do cartão. */
const SETOR = {
  // VT: o trio nomeia o NÚMERO por onde a conversa veio.
  // ★★★ A-8.1 (14/out/2026): 'Evolution' → 'Vendas'. ⚠⚠ Este chip é o texto mais lido do
  // sistema — aparece em CADA cartão da inbox, o dia inteiro, para toda SDR. Trocar só o
  // rótulo de Configurações teria deixado a palavra justamente onde ela mais aparece.
  marketing_entrada: 'API Oficial',
  marketing_resgate: 'Vendas',
  recepcao: 'Recepção',
};
const setorDaConversa = (c) => SETOR[c.numeroPapel] ?? PAPEIS_NUMERO[c.numeroPapel]?.label ?? null;

/**
 * Com acordeão o estado é UM id (ou null), não um objeto de booleanos — é a
 * diferença entre "quais estão abertas" e "qual está aberta".
 *
 * Qual abre no começo deixou de ser constante: agora vem de `secaoPadrao`, que
 * lê a preferência da pessoa (`secoesInbox.js`).
 */

function ItemConversa({ c, etiquetas, ativa, aoEscolher, clinicaId, atendente, equipe = [] }) {
  const [assumindo, setAssumindo] = useState(false);
  const [erro, setErro] = useState(null);

  const etiquetasDaConversa = (c.etiquetas ?? [])
    .map((id) => etiquetas.find((e) => e.id === id))
    .filter(Boolean);

  const nome = nomeDoContato(c);
  const setor = setorDaConversa(c);
  // ★ WA — reserva: recalculada a cada render da lista (o listener redesenha a cada mensagem, e o minuto
  // que falta é informação de relance, não um cronômetro).
  const agora = new Date();
  // ★★ PC-6: clínica com 1 SDR (dono, 24/10) — "não precisa ficar aparecendo aquela mensagem de lead
  // reservado"; o comportamento da reserva continua (rules e callable), só a pílula some.
  const rotulo = clinicaDeUmaSdr(equipe) ? '' : rotuloDaReserva(c, atendente, agora);
  const [avisoPosse, setAvisoPosse] = useState(null);
  const reservadaParaMim = Boolean(rotulo) && c.reservadaParaId === atendente?.id;
  const podeAtenderAgora = podeAtender(c, atendente, agora);

  async function atender(evento) {
    // Sem isto o clique borbulha para o cartão e abre a conversa junto — o
    // atendente pediria "Atender" e ganharia a thread inteira na cara.
    evento.stopPropagation();
    setErro(null);
    setAssumindo(true);
    try {
      // ★ PC-6 R5: a conversa é de quem atendeu; o lead pode ter ficado com a dona.
      setAvisoPosse(avisoDaPuxada(await assumirConversa(clinicaId, c.id), atendente?.id, equipe));
    } catch (e) {
      console.error('[OdontoSDR] Falha ao assumir a conversa:', e);
      // ★ WA: a recusa por reserva vem do servidor com a mensagem pronta (de quem é a vez e por quanto tempo).
      setErro(e?.code === 'functions/failed-precondition' ? e.message : 'Não foi possível assumir.');
    } finally {
      setAssumindo(false);
    }
  }

  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        className={ativa ? 'conversa-item ativa' : 'conversa-item'}
        onClick={aoEscolher}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            aoEscolher();
          }
        }}
      >
        <AvatarContato foto={c.contatoFoto} nome={nome} />

        <div className="conversa-corpo">
          <div className="conversa-topo">
            <strong className="conversa-nome">{nome}</strong>
            <span className="conversa-hora">{quando(c.atualizadoEm)}</span>
          </div>

          <p className="conversa-previa">
            {c.ultimaMensagem?.interna && <span className="marca-nota">nota</span>}
            {/* ★ WB: a última mensagem não saiu — a fila mostra isso sem precisar abrir a conversa. */}
            {rotuloDaEntrega({ direcao: 'saida', ...c.ultimaMensagem }, { curto: true }) && (
              <span className="marca-nao-enviada">{rotuloDaEntrega({ direcao: 'saida', ...c.ultimaMensagem }, { curto: true })}</span>
            )}
            {c.ultimaMensagem?.texto ?? 'Sem mensagens'}
          </p>

          <div className="conversa-rodape">
            {setor && <span className="chip-setor">{setor}</span>}

            {c.atendimento === 'aguardando_ia' && (
              <span className="etiqueta ia" title="Conversa de entrada, destinada à SDR IA">
                IA
              </span>
            )}

            {etiquetasDaConversa.map((e) => (
              <span key={e.id} className="etiqueta-cor" style={{ background: e.cor }}>
                {e.nome}
              </span>
            ))}

            {c.naoLidas > 0 && <span className="badge-nao-lidas">{c.naoLidas}</span>}

            {/* Quem já tem dono mostra o dono; quem não tem, mostra a ação de
                virar dono. Nunca os dois — o rodapé responde a uma pergunta só:
                "quem está cuidando disto?"

                GRUPO não entra nessa pergunta: ninguém "assume" um grupo de
                laboratório, e oferecer "Atender" prometeria uma responsabilidade
                que o produto não modela. */}
            {/* ★ WA — a RESERVA: enquanto vale, a vez é de quem o rodízio escolheu. O aviso aparece para
                todo mundo (quem tem a vez precisa saber que a tem; quem não tem precisa saber por quê), e o
                "Atender" some de quem não pode — o gestor sempre pode, e a rule é a autoridade. */}
            {rotulo && <span className={reservadaParaMim ? 'pilula-reserva minha' : 'pilula-reserva'} title={rotulo}>{rotulo}</span>}

            {c.ehGrupo ? null : mostraDono(c) ? (
              /* Em DESTAQUE, e no lugar exato onde ficaria o "Atender": quem
                 varre a fila procura justamente isto — "esta é de quem?". O
                 texto discreto de antes se perdia entre as etiquetas. */
              <span className="pilula-responsavel" title={`Em atendimento por ${c.responsavelNome}`}>
                {c.responsavelNome}
              </span>
            ) : podeAtenderAgora ? (
              <button
                type="button"
                className="btn-atender"
                onClick={atender}
                disabled={assumindo}
              >
                {assumindo ? '…' : 'Atender'}
              </button>
            ) : null}
          </div>

          {erro && <p className="erro compacto">{erro}</p>}
          {avisoPosse && <p className="aviso compacto" data-campo="aviso-posse">{avisoPosse}</p>}
        </div>
      </div>
    </li>
  );
}

export default function ListaConversas() {
  const {
    conversasFiltradas,
    filtros,
    setFiltros,
    limparFiltros,
    etiquetas,
    conversa,
    selecionar,
    carregando,
    canal,
    clinicaId,
    atendente,
    conversas,
    equipe,
    espaco,
    contagens,
    numeros,
  } = useConversas();

  // "Resgate / entrada de leads" enquanto a flag do número estiver ligada.
  const legendaCanal = legendaDoCanal(canal, numeros);

  // Acordeão: guarda QUAL seção está aberta, ou null quando nenhuma está.
  //
  // Preservado entre montagens: ir ao funil e voltar recolhia "Em atendimento"
  // — justamente a seção onde está a conversa que se estava atendendo — e
  // reabria "Aguardando". Chave única, sem o canal: é preferência de exibição,
  // e quem abre "Em atendimento" quer isso nos três canais.
  const secoes = useSecoesInbox(atendente?.id ?? null);
  const visiveis = secoesVisiveisOrdenadas(secoes.preferencias);
  const padraoDaPessoa = secaoPadrao(secoes.preferencias);

  const [aberta, setAberta] = useEstadoPreservado('inbox:secaoAberta', padraoDaPessoa);
  const [iniciando, setIniciando] = useState(false);
  const [ajustandoConfig, setAjustandoConfig] = useState(false);

  /**
   * O som escuta a lista AO VIVO do canal (`conversas`), não a filtrada.
   *
   * Com a filtrada, digitar na busca calaria o inbox: as conversas que não
   * casassem com o termo sumiriam do snapshot, e a próxima que chegasse fora do
   * filtro não existiria para a comparação. O aviso sonoro é sobre o que
   * acontece no canal, não sobre o que está visível na tela.
   */
  const som = useNotificacaoSom(conversas, {
    abertaId: conversa?.id ?? null,
    uid: atendente?.id ?? null,
    canalId: canal?.id ?? null,
  });

  const mudar = (campo) => (e) => setFiltros((f) => ({ ...f, [campo]: e.target.value }));
  const alternarFiltro = (campo) => () => setFiltros((f) => ({ ...f, [campo]: !f[campo] }));

  /**
   * Quem pode aparecer como responsável NESTE espaço.
   *
   * Mesma regra do `DialogoTransferencia` (e a mesma constante, agora
   * compartilhada): oferecer um SDR no seletor da Recepção só produziria lista
   * vazia, porque conversa de recepção nunca teve um SDR como responsável.
   */
  const responsaveis = equipe.filter(
    (u) => u.ativo !== false && PAPEIS_POR_ESPACO[espaco]?.includes(u.papel),
  );
  // Clicar na que já está aberta fecha; clicar em outra troca a aberta.
  const alternar = (id) => setAberta((atual) => (atual === id ? null : id));

  const grupos = agruparPorEstado(conversasFiltradas);
  const vazia = conversasFiltradas.length === 0;

  /**
   * Ocultar a seção que estava aberta não pode deixar o acordeão apontando para
   * o nada. Derivado na renderização, sem efeito: um `useEffect` corrigindo o
   * estado renderizaria uma vez com o valor órfão antes de consertar.
   */
  const abertaEfetiva = visiveis.some((sec) => sec.id === aberta) ? aberta : padraoDaPessoa;

  /**
   * A pessoa ocultou justamente as seções que têm conversa.
   *
   * Não é erro nem lista vazia: as conversas existem, só não há onde mostrá-las.
   * Sem esta mensagem a lista ficaria um retângulo branco sem explicação, e o
   * caminho de volta (a engrenagem) não estaria em lugar nenhum.
   */
  const tudoOculto = !vazia && visiveis.every((sec) => grupos[sec.id].length === 0);

  return (
    <section className="inbox-lista" aria-label="Conversas">
      <div className="inbox-filtros">
        {legendaCanal && (
          <p className="indicador-nota inbox-legenda-canal" title="Este número cria lead novo a cada contato">
            {legendaCanal}
          </p>
        )}

        {/* Só nos canais Evolution. Na via oficial da Meta, iniciar conversa
            exige mensagem-modelo aprovada — o botão viria com promessa que o
            envio não cumpriria. */}
        {podeIniciarConversa(canal) && (
          <button
            type="button"
            className="btn-primario compacto btn-iniciar-conversa"
            onClick={() => setIniciando(true)}
          >
            + Iniciar conversa
          </button>
        )}

        <div className="inbox-busca-linha">
          <input
            type="search"
            placeholder="Buscar por nome, telefone ou mensagem…"
            value={filtros.busca}
            onChange={mudar('busca')}
            aria-label="Buscar conversas"
          />

          {/* A engrenagem mora ao lado da busca porque é ajuste DA LISTA, não
              da conversa aberta — e o ponto verde diz, sem abrir o painel, se o
              som está ligado. Sem ele a pessoa não teria como saber por que o
              inbox está calado. */}
          <button
            type="button"
            className={som.preferencias.geral ? 'btn-engrenagem ligado' : 'btn-engrenagem'}
            onClick={() => setAjustandoConfig(true)}
            aria-label="Configurações da lista"
            title={
              som.preferencias.geral
                ? 'Configurações — som ligado'
                : 'Configurações — som desligado'
            }
          >
            <span aria-hidden="true">⚙</span>
          </button>
        </div>

        {/* As abas de fila e o seletor de status saíram: as seções abaixo já
            dizem o estado de cada conversa, e manter as duas coisas faria o
            usuário filtrar por status para depois procurar a seção do mesmo
            status. A busca e a etiqueta ficam, porque cortam na transversal. */}
        {/* Atalhos do dia a dia, combináveis entre si e com tudo o mais. A
            contagem ao lado responde "vale a pena marcar?" antes do clique —
            por isso conta o canal inteiro, não o resultado já filtrado. */}
        <div className="inbox-atalhos">
          <label className="atalho">
            <input
              type="checkbox"
              checked={filtros.apenasNaoLidas}
              onChange={alternarFiltro('apenasNaoLidas')}
            />
            Não lidas
            <span className="atalho-contagem">{contagens.naoLidas}</span>
          </label>

          <label className="atalho">
            <input
              type="checkbox"
              checked={filtros.apenasMinhas}
              onChange={alternarFiltro('apenasMinhas')}
            />
            Meus atendimentos
            <span className="atalho-contagem">{contagens.minhas}</span>
          </label>
        </div>

        <div className="inbox-filtros-linha">
          <select
            value={filtros.responsavel}
            onChange={mudar('responsavel')}
            aria-label="Responsável"
          >
            <option value={RESPONSAVEL_TODOS}>Qualquer responsável</option>
            {/* "Sem responsável" é um valor especial, não um uid — é a fila que
                ninguém pegou, e ela não tem dono para se listar aqui. */}
            <option value={RESPONSAVEL_SEM}>Sem responsável</option>
            {responsaveis.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome || u.email}
              </option>
            ))}
          </select>

          <select value={filtros.etiqueta} onChange={mudar('etiqueta')} aria-label="Etiqueta">
            <option value="">Todas as etiquetas</option>
            {etiquetas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>

          {temFiltroAtivo(filtros) && (
            <button type="button" className="btn-texto" onClick={limparFiltros}>
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* UM contêiner de rolagem para todas as seções — mesmo padrão do
          `.thread`. Antes o `overflow-y` morava na `.conversas`, aninhada
          dentro da `.lista-secao`, que é um div comum: sem um pai flex de
          altura limitada, o `flex: 1` não tinha o que dividir, a lista crescia
          além da caixa e o `overflow: hidden` da `.inbox-lista` cortava o
          excedente sem barra nenhuma. Os filtros ficam FORA, parados no topo;
          o cabeçalho de cada seção rola junto com os cartões dela. */}
      <div className="lista-corpo">
        {!carregando && vazia && (
          // Sem filtro ativo, culpar os filtros manda o atendente procurar
          // defeito onde não há: o canal simplesmente ainda não tem conversa. O
          // caso de Leads é esperado até o número oficial da Meta entrar no ar.
          <p className="vazio">
            {temFiltroAtivo(filtros)
              ? 'Nenhuma conversa com esses filtros.'
              : canal?.id === 'leads'
                ? 'Nenhuma conversa ainda. Esta é a caixa da API Oficial da Meta — o número ainda não foi conectado.'
                : 'Nenhuma conversa neste canal ainda.'}
          </p>
        )}

        {tudoOculto && (
          <p className="vazio">
            Há conversas neste canal, mas as seções que as contêm estão ocultas.
            Ajuste em <strong>Configurações</strong> (⚙, ao lado da busca).
          </p>
        )}

        {!vazia &&
          visiveis.map((secao) => {
            const daSecao = grupos[secao.id];
            // Seção vazia some: três títulos sempre presentes, dois deles
            // zerados, só ocupariam a altura útil da lista.
            if (daSecao.length === 0) return null;

            const estaAberta = abertaEfetiva === secao.id;
            return (
              <div key={secao.id} className="lista-secao">
                <button
                  type="button"
                  className="lista-secao-titulo"
                  onClick={() => alternar(secao.id)}
                  aria-expanded={estaAberta}
                >
                  <span className={estaAberta ? 'seta aberta' : 'seta'} aria-hidden="true">
                    ▸
                  </span>
                  {secao.label}
                  <span className="lista-secao-contagem">{daSecao.length}</span>
                  {/* Só aparece quando há o que ler. Uma bolinha "0" seria ruído
                      permanente em quatro seções. */}
                  {contarNaoLidas(daSecao) > 0 && (
                    <span
                      className="lista-secao-novas"
                      title={`${contarNaoLidas(daSecao)} com mensagem não lida`}
                    >
                      {contarNaoLidas(daSecao)}
                    </span>
                  )}
                </button>

                {estaAberta && (
                  <ul className="conversas">
                    {daSecao.map((c) => (
                      <ItemConversa
                        key={c.id}
                        c={c}
                        etiquetas={etiquetas}
                        ativa={conversa?.id === c.id}
                        aoEscolher={() => selecionar(c.id)}
                        clinicaId={clinicaId}
                        atendente={atendente}
                        equipe={equipe}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
      </div>

      <DialogoIniciarConversa aberto={iniciando} aoFechar={() => setIniciando(false)} />

      <PainelConfiguracoes
        aberto={ajustandoConfig}
        aoFechar={() => setAjustandoConfig(false)}
        som={som}
        secoes={secoes}
      />
    </section>
  );
}
